import { CordataWemTriple } from "@biblioteksentralen/bmdb-search-client";
import {
  Contributor,
  Expression,
  formatIsbdTitle,
  FormatIsbdTitleOptions,
  Manifestation,
  Role,
} from "@biblioteksentralen/cordata";
import { defaultFormatOrder, getDocumentTypeLabel, getFormatOrderIndex } from "./documentTypes";
import { CordataWork } from "./CordataWork";
import { chooseCoverImageQuality, isValidCoverImage } from "./coverImage";
import {
  CordataLanguageCode,
  formatLanguagesList,
  getLanguagesSignature,
  getSortedLanguagesListIndex,
  handleLanguages,
  LanguageListFormatOptions,
} from "./languages";
import { LibrarySystem } from "@libry-content/types";
import { slugifyString, sortByMultiple } from "@biblioteksentralen/utils";
import { getExternalContentService } from "./externalContentService";
import { sift, unique } from "radash";
import { getPath, ResolvedSite } from "@libry-content/common";
import { getLibrarySystemClient } from "../librarySystem";
import { CordataVariant } from "./CordataVariant";

export const sortWorkTriples = (workTriples: CordataWorkTriple[]) =>
  sortByMultiple(
    workTriples,
    ({ languages }) => getSortedLanguagesListIndex(languages),
    ({ documentType }) => getFormatOrderIndex(documentType?.format, defaultFormatOrder),
    ({ isAggregateManifestation }) => (isAggregateManifestation ? 2 : 1),
    ({ work, hasPartTitles: manifestationHasPartTitles }) =>
      work.title.titleType! === "uniform_title_music" && work.hasPartTitles === manifestationHasPartTitles ? 1 : 2,
    ({ publicationYear }) => (isNaN(Number(publicationYear)) ? 0 : -Number(publicationYear)),
    ({ coverImageUrl }) => (isValidCoverImage(coverImageUrl) ? 1 : 2)
  );

export const isCordataWorkTriple = (item: unknown): item is CordataWorkTriple =>
  !!item && typeof item === "object" && (item as CordataWorkTriple)?.className === "CordataWorkTriple";

export class CordataWorkTriple {
  public className = "CordataWorkTriple" as const;
  public work: CordataWork;
  private expression: Expression;
  private manifestation: Manifestation;
  public isAggregateManifestation: boolean;

  constructor(workTriple: CordataWemTriple) {
    this.work = new CordataWork(workTriple.work);
    this.expression = workTriple.expression;
    this.manifestation = workTriple.manifestation;
    this.isAggregateManifestation = workTriple.isAggregateManifestation;
  }

  // TODO: We dont want to provide manifestation directly, so at a later stage we should be using workTriple instead wherever manifestation is needed
  /**@deprecated */
  public getRawBmdbManifestation() {
    return this.manifestation;
  }

  // TODO: CordataWorkTriple gjør det samme som SortableManifestation i prinsippet, og skal brukes istedet på sikt
  /**@deprecated */
  public getSortableManifestation = (
    preferredLanguageCodeOrder?: readonly CordataLanguageCode[]
  ): SortableManifestation => ({
    ...this.manifestation,
    collections: this.manifestationCollections,
    expression: {
      ...this.expression,
      languages: handleLanguages(preferredLanguageCodeOrder)(this.languages),
      collections: this.expressionCollections,
    },
  });

  public get cordataVariant() {
    return new CordataVariant(this);
  }

  public get manifestationId() {
    return this.manifestation.id;
  }

  public get manifestationPath() {
    return `${this.work.path}/utgave/${slugifyString(this.title.mainTitle)}_${this.manifestation.id}`;
  }

  public get workTriplePath() {
    return `${this.work.path}?manifestationId=${this.manifestation.id}`;
  }

  public get coverImageUrl() {
    return chooseCoverImageQuality(this.manifestation.coverImage, "thumbnail");
  }

  public get expressionContributors() {
    return getContributorsWithPath(this.expression.contributors);
  }

  public get manifestationContributors() {
    return getContributorsWithPath(this.manifestation.contributors);
  }

  public get allContributors() {
    return getContributorsWithPath([
      ...this.work.contributors,
      ...this.expressionContributors,
      ...this.manifestationContributors,
    ]);
  }

  public get mainContributor() {
    return this.allContributors.find(({ isMainContributor }) => isMainContributor);
  }

  public getContributorsWithRoles(roleCodes: Role["code"][]) {
    return getContributorsWithPath(
      unique(this.allContributors, ({ agent }) => agent.id)
        .filter(({ roles }) => roles.some(({ code }) => roleCodes.includes(code)))
        .map(({ roles, ...rest }) => ({ ...rest, roles: roles.filter(({ code }) => roleCodes.includes(code)) }))
    );
  }

  public get publicationYear() {
    return this.manifestation.publicationYear;
  }

  public get isbns() {
    return this.manifestation.isbn;
  }

  public get languages() {
    return this.expression.languages;
  }

  public get spokenLanguages() {
    return this.work.isMovie && !!this.languages ? this.languages : undefined;
  }

  public getSpokenLanguagesFormatted(options?: LanguageListFormatOptions) {
    return this.spokenLanguages && formatLanguagesList(this.spokenLanguages, options);
  }

  public get languagesSignature() {
    return getLanguagesSignature(this.expression.languages);
  }

  public get title() {
    return this.manifestation.title;
  }

  public get hasPartTitles() {
    return !!this.title.partTitle || !!this.title.partNumber;
  }

  public get originalLanguageTitle() {
    return !!this.work.title.titleLanguage &&
      !this.expression?.languages?.some(({ code }) => !!code && code === this.work.title.titleLanguage?.code)
      ? this.work.formattedTitle
      : undefined;
  }

  public get parallelTitles() {
    return sift(this.manifestation.parallelTitles.map((title) => formatIsbdTitle({ ...this.manifestation, title })));
  }

  public getFormattedManifestationTitle(options?: FormatIsbdTitleOptions) {
    return formatIsbdTitle({ title: this.title }, options);
  }

  public getFormattedExpressionTitle(options?: FormatIsbdTitleOptions) {
    return this.expression.title && formatIsbdTitle({ title: this.expression.title }, options);
  }

  public get documentType() {
    return this.manifestation.documentType;
  }

  public get documentTypeLabel() {
    const documentType = this.documentType;
    if (!documentType) return undefined;
    const { medium, format } = documentType;
    if (medium === "strømningsbar video") return "Strømmevideo";
    if (medium && ["Film", "Musikk", "Dataspill"].includes(format)) return medium;
    if (medium === "kassett") return `Kassett ${format}`;
    return format;
  }

  public get signatures() {
    return this.manifestation._signatures;
  }

  public get platforms() {
    return this.expression.platforms;
  }

  public get numberOfPlayers() {
    return this.expression.numberOfPlayers;
  }

  public get origin() {
    return this.manifestation._origin;
  }

  public get publicationStatements() {
    return this.manifestation.publicationStatements;
  }

  public get relatedExpressions() {
    return this.manifestation.expressions;
  }

  public get ean() {
    return this.manifestation.ean;
  }

  public get binding() {
    return this.manifestation.binding;
  }

  public get expressionCollections() {
    return this.expression.collections;
  }

  public get manifestationCollections() {
    return this.manifestation.collections;
  }

  public get collections() {
    return [...this.manifestationCollections, ...this.expressionCollections];
  }

  public get externalContentService() {
    return getExternalContentService(this.origin);
  }

  public getLocalRecord(librarySystem: LibrarySystem | undefined) {
    if (!librarySystem?.database) return undefined;
    return this.manifestation.libraryCatalogueRecords?.find((record) => {
      const databaseName = librarySystem.bibliofilInstance ?? librarySystem.database;
      return record.catalogueId === databaseName;
    });
  }

  public get mediaTypeLabel() {
    const documentType = this.documentType;
    const illustratedTag = this.work.illustratedTag;
    const platformLabel = this.platformsLabel;

    const documentTypeLabel = documentType ? getDocumentTypeLabel(documentType) : undefined;
    const mediaTypeLabel = illustratedTag ?? platformLabel ?? documentTypeLabel;
    return mediaTypeLabel;
  }

  public isInCatalogue(librarySystem: LibrarySystem) {
    //check if manifestation has a record for the catalogue
    const catalogueIdMatch = this.manifestation.libraryCatalogueRecords?.find(
      (libraryCatalogueRecord) => libraryCatalogueRecord.catalogueId === librarySystem.database
    );

    //if catalogueId is provided and the catalogue record is for filmoteket, check if the catalogueId has the filmoteket-manifestation
    const filmoteketMatch = this.manifestation.libraryCatalogueRecords?.find(
      (libraryCatalogueRecord) =>
        libraryCatalogueRecord.catalogueId === "filmoteket" &&
        libraryCatalogueRecord.libraryIds?.find((libraryId) => libraryId === librarySystem.database)
    );

    if (catalogueIdMatch || filmoteketMatch) {
      return true;
    }

    return false;
  }

  public get platformsLabel() {
    return this.expression.platforms?.length
      ? this.expression.platforms.map((platform) => platform.abbreviatedName ?? platform.name).join(" & ")
      : undefined;
  }

  public getExternalPathToRecord(site?: ResolvedSite) {
    if (!site) return undefined;
    const librarySystemClient = getLibrarySystemClient(site.librarySystem);
    const publicationId = this.getLocalRecord(site.librarySystem)?.localRecordId;
    if (publicationId) {
      const publicationUrl = librarySystemClient?.getLinkToRecord(publicationId);
      return publicationUrl;
    }
    return undefined;
  }
}

export type ExpressionSummary<T extends Expression> = Pick<
  T,
  "languages" | "collections" | "contributors" | "title" | "platforms" | "numberOfPlayers"
>;

export type SortableManifestation = Manifestation & {
  isAggregateManifestation?: boolean;
  expression: ExpressionSummary<Expression>;
};

export const getContributorsWithPath = (contributors: Contributor[]) =>
  contributors.map(({ agent, ...rest }) => ({
    ...rest,
    agent: { ...agent, href: agent._origin?.name === "promus" ? getPath(agent) : undefined },
  }));
